import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../app/http-common';
import { useCanEdit, useCanCreateBrief, useIsAdmin, useUserInfo } from '../../hooks/useFrontegg';
import { SAVED_BRIEF_READY_EVENT, SAVED_BRIEF_CREATED_EVENT } from './events';
import MyBriefsFilters from './MyBriefsFilters';
import MyBriefsList from './MyBriefsList';
import {
  MY_BRIEF_DEFAULT_FILTERS,
  buildBriefCopyHtml,
  getBriefBodyPlainTextWithLinks,
  isActiveStatus,
  normalizeBriefBodyForEditor,
  resolveSavedBriefResult,
} from './myBriefsUtils';
import { copyRichText } from '../../utils/clipboard';
import BriefGenerationSettingsModal from '../CreateBrief/BriefGenerationSettingsModal';
import { toIso2, OUTPUT_LANGUAGE_NAMES } from '../../utils/languages';
import './MyBriefs.css';

const PAGE_SIZE = 20;
const AUTOSAVE_DEBOUNCE_MS = 2000;

function draftsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.header === b.header && a.subheader === b.subheader && a.brief === b.brief;
}

function getEditableDraft(detail, preferredVariant = null) {
  const resolvedResult = resolveSavedBriefResult(detail, preferredVariant);
  return {
    header: resolvedResult.content?.header || '',
    subheader: resolvedResult.content?.subheader || '',
    brief: normalizeBriefBodyForEditor(resolvedResult.content?.brief || ''),
  };
}

// Builds both clipboard flavors for a draft: sanitized rich HTML for editors
// that accept it, and a link-aware plain-text rendering for everything else.
function buildCopyPayload(item, detail, draft, preferredVariant = null) {
  const empty = { html: '', text: '' };
  if (!detail) return empty;
  const status = detail.status;
  const resolvedResult = resolveSavedBriefResult(detail, preferredVariant);

  let header = '';
  let subheader = '';
  let body = '';

  if (status === 'ready') {
    header = draft?.header || resolvedResult.content?.header || '';
    subheader = draft?.subheader || resolvedResult.content?.subheader || '';
    body = draft?.brief || resolvedResult.content?.brief || '';
  } else if (status === 'failed' && detail.partialContent) {
    body = detail.partialContent;
  } else if (status === 'failed' && resolvedResult.content?.brief) {
    header = resolvedResult.content?.header || '';
    subheader = resolvedResult.content?.subheader || '';
    body = resolvedResult.content?.brief || '';
  } else {
    return empty;
  }

  // Match the legacy /draft copy format: just headline + subheader + body,
  // no "Title:" / "Language:" / "Status:" / "Header:" / "Subheader:" labels.
  const sections = [];
  if (header) sections.push(header);
  if (subheader) sections.push(subheader);
  const plainBody = getBriefBodyPlainTextWithLinks(body);
  if (plainBody) sections.push(...(sections.length ? ['', plainBody] : [plainBody]));

  return {
    html: buildBriefCopyHtml({ header, subheader, body }),
    text: sections.join('\n'),
  };
}

function buildGenerationSettings(detail, item) {
  const payload = detail?.requestPayload || {};
  return {
    language: payload.language || detail?.language || item?.language || 'English',
    promptType: payload.promptType || detail?.promptType || 'DEFAULT_BRIEF_2_CITATIONS',
    model: payload.model || detail?.model || 'gpt-5',
    translationModel: payload.translationModel || detail?.translationModel || 'gpt',
    generationMethod: payload.generationMethod || detail?.generationMethod || 'claims',
    overwriteExisting: false,
  };
}

function mergeSavedBriefIntoListItem(item, detail) {
  const incomingStatus = detail?.status || item.status;
  const preserveStableStatus = !isActiveStatus(item.status) && isActiveStatus(incomingStatus);
  return {
    ...item,
    status: preserveStableStatus ? item.status : incomingStatus,
    language: detail?.language || item.language,
    storyTitle: detail?.storyTitle || item.storyTitle,
    selectedArticleCount: Number.isFinite(detail?.selectedArticleCount)
      ? detail.selectedArticleCount
      : item.selectedArticleCount,
  };
}

function mergeBackgroundBriefs(existingItems, incomingItems) {
  const existingIds = new Set(existingItems.map((item) => String(item._id)));
  const incomingMap = new Map(incomingItems.map((item) => [String(item._id), item]));
  const prependedNewItems = incomingItems.filter((item) => !existingIds.has(String(item._id)));
  const mergedExistingItems = existingItems.map((item) => {
    const incoming = incomingMap.get(String(item._id));
    return incoming ? { ...item, ...incoming } : item;
  });
  return prependedNewItems.concat(mergedExistingItems);
}

function MyBriefsPage() {
  const location = useLocation();
  const canEdit = useCanEdit();
  const canCreateBrief = useCanCreateBrief();
  const isAdmin = useIsAdmin();
  const userInfo = useUserInfo();
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState(MY_BRIEF_DEFAULT_FILTERS);
  const [draftSearch, setDraftSearch] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [savedBriefLanguages, setSavedBriefLanguages] = useState([]);
  const [savedBriefOwners, setSavedBriefOwners] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [detailById, setDetailById] = useState({});
  const [detailLoadingById, setDetailLoadingById] = useState({});
  const [editDraftById, setEditDraftById] = useState({});
  const [comparisonViewById, setComparisonViewById] = useState({});
  const [dirtyById, setDirtyById] = useState({});
  const [savingById, setSavingById] = useState({});
  const [saveStatusById, setSaveStatusById] = useState({});
  const [translatingById, setTranslatingById] = useState({});
  const [translateModal, setTranslateModal] = useState(null); // { id, target }
  // Per-brief cache of source-language snapshots so that retranslating back to
  // a language we've already produced (typically the original English) just
  // restores the cached headline suggestions / header / subheader / body
  // instead of round-tripping through the translator.
  // Shape: { [briefId]: { [langCode]: { titles, header, subheader, brief } } }
  const [originalsByLangById, setOriginalsByLangById] = useState({});
  const [nowMs, setNowMs] = useState(Date.now());
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [generationModal, setGenerationModal] = useState(null);
  const [publishDialog, setPublishDialog] = useState(null);
  const [publishClients, setPublishClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [publishStatus, setPublishStatus] = useState('draft');
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  const lastHandledExpandRequestRef = useRef('');
  const loadMoreSentinelRef = useRef(null);
  const toastTimerRef = useRef(null);
  const itemsRef = useRef(items);
  const autosaveTimersRef = useRef({});
  const editDraftByIdRef = useRef(editDraftById);
  const detailByIdRef = useRef(detailById);
  const comparisonViewByIdRef = useRef(comparisonViewById);
  const saveStatusByIdRef = useRef(saveStatusById);
  const dirtyByIdRef = useRef(dirtyById);

  useEffect(() => { editDraftByIdRef.current = editDraftById; }, [editDraftById]);
  useEffect(() => { detailByIdRef.current = detailById; }, [detailById]);
  useEffect(() => { comparisonViewByIdRef.current = comparisonViewById; }, [comparisonViewById]);
  useEffect(() => { saveStatusByIdRef.current = saveStatusById; }, [saveStatusById]);
  useEffect(() => { dirtyByIdRef.current = dirtyById; }, [dirtyById]);


  const hasUnsavedChanges = useMemo(
    () => Object.values(dirtyById).some(Boolean),
    [dirtyById]
  );

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    Object.values(autosaveTimersRef.current).forEach((timer) => window.clearTimeout(timer));
    autosaveTimersRef.current = {};
  }, []);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const buildListParams = useCallback((currentOffset) => ({
    limit: PAGE_SIZE,
    offset: currentOffset,
    scope: isAdmin && filters.ownerId !== 'mine' ? 'all' : 'mine',
    ownerId: isAdmin && filters.ownerId !== 'mine' ? filters.ownerId : null,
    status: filters.status,
    language: filters.language,
    dateRange: filters.dateRange,
    q: submittedQuery,
    sort: filters.sort,
    contentType: filters.contentType,
  }), [filters, isAdmin, submittedQuery]);

  const loadLanguages = useCallback(async () => {
    try {
      const response = await api.listSavedBriefLanguages({
        scope: isAdmin && filters.ownerId !== 'mine' ? 'all' : 'mine',
        ownerId: isAdmin && filters.ownerId !== 'mine' ? filters.ownerId : null,
      });
      setSavedBriefLanguages(Array.isArray(response?.languages) ? response.languages : []);
    } catch (_err) {
      setSavedBriefLanguages([]);
    }
  }, [filters.ownerId, isAdmin]);

  const loadOwners = useCallback(async () => {
    if (!isAdmin) {
      setSavedBriefOwners([]);
      return;
    }
    try {
      const response = await api.listSavedBriefOwners();
      setSavedBriefOwners(Array.isArray(response?.owners) ? response.owners : []);
    } catch (_err) {
      setSavedBriefOwners([]);
    }
  }, [isAdmin]);

  const loadBriefs = useCallback(async ({ mode = 'replace', nextOffset = 0, limitOverride = null } = {}) => {
    const isAppend = mode === 'append';
    const isBackgroundMerge = mode === 'background_merge';

    if (isAppend) {
      setLoadingMore(true);
    } else if (!isBackgroundMerge) {
      setLoadingInitial(true);
      setError('');
    }

    try {
      const params = buildListParams(nextOffset);
      if (limitOverride) {
        params.limit = limitOverride;
      }
      const response = await api.listSavedBriefs(params);
      const nextItems = Array.isArray(response?.items) ? response.items : [];
      const resolvedNextOffset = Number(response?.nextOffset ?? (nextOffset + nextItems.length));
      const resolvedHasMore = Boolean(response?.hasMore);
      const resolvedTotal = Number(response?.total ?? nextItems.length);

      if (isBackgroundMerge) {
        const mergedItems = mergeBackgroundBriefs(itemsRef.current, nextItems);
        setItems(mergedItems);
        setOffset(Math.max(mergedItems.length, resolvedNextOffset));
        setHasMore(mergedItems.length < resolvedTotal);
      } else {
        setItems((prev) => {
          if (isAppend) {
            const existingIds = new Set(prev.map((item) => String(item._id)));
            const uniqueIncoming = nextItems.filter((item) => !existingIds.has(String(item._id)));
            return prev.concat(uniqueIncoming);
          }
          return nextItems;
        });
        setOffset(resolvedNextOffset);
        setHasMore(resolvedHasMore);
      }
      setNowMs(Date.now());
    } catch (err) {
      if (!isBackgroundMerge) {
        setError(err?.response?.data?.message || err?.message || 'Failed to load saved drafts.');
      }
    } finally {
      if (!isBackgroundMerge) {
        setLoadingInitial(false);
      }
      if (isAppend) {
        setLoadingMore(false);
      }
    }
  }, [buildListParams]);

  const clearAutosaveTimer = useCallback((idStr) => {
    const timer = autosaveTimersRef.current[idStr];
    if (timer) {
      window.clearTimeout(timer);
      delete autosaveTimersRef.current[idStr];
    }
  }, []);

  // Intercept SPA navigation away from /my-drafts while there are unsaved or
  // in-flight edits. Routes through the existing custom confirm modal
  // (my-briefs-confirm-modal) instead of the browser's beforeunload dialog.
  // Covers React Router navigate() / <Link> (via history.pushState) and
  // browser back/forward (popstate). Hard reloads / tab close / clicks that
  // assign window.location.href are not interceptable without beforeunload.
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const startUrl = window.location.pathname + window.location.search + window.location.hash;
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;
    let releasing = false;

    const requestConfirm = (proceed) => {
      setConfirmDialog({
        title: 'Unsaved changes',
        message: 'You have unsaved changes on this page. Leave without saving?',
        confirmLabel: 'Leave',
        onConfirm: () => {
          releasing = true;
          Object.keys(autosaveTimersRef.current).forEach(clearAutosaveTimer);
          setDirtyById({});
          setSaveStatusById({});
          proceed();
        },
      });
    };

    const wrapHistory = (orig) => function guardedHistoryMethod(state, title, url) {
      if (releasing || url == null) {
        return orig.apply(this, arguments);
      }
      const target = String(url);
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      if (target === currentUrl) {
        return orig.apply(this, arguments);
      }
      const args = [state, title, url];
      requestConfirm(() => {
        orig.apply(window.history, args);
        window.dispatchEvent(new PopStateEvent('popstate'));
      });
      return undefined;
    };

    window.history.pushState = wrapHistory(originalPush);
    window.history.replaceState = wrapHistory(originalReplace);

    const onPopState = () => {
      if (releasing) return;
      const targetUrl = window.location.pathname + window.location.search + window.location.hash;
      if (targetUrl === startUrl) return;
      originalPush.call(window.history, null, '', startUrl);
      requestConfirm(() => {
        originalPush.call(window.history, null, '', targetUrl);
        window.dispatchEvent(new PopStateEvent('popstate'));
      });
    };
    window.addEventListener('popstate', onPopState);

    // Capture-phase click guard for hard-nav triggers that bypass history API:
    // - .logo-button (does window.location.href = '/')
    // - .user-menu-item Logout (Frontegg menuLogout hard-redirects)
    // - any <a href> internal/external link click that would unload the page
    // Capture phase runs before React's root listener, so preventDefault +
    // stopImmediatePropagation prevents the original onClick from firing.
    // On confirm we replay the action with our listener temporarily detached.
    const onCaptureClick = (event) => {
      if (releasing || event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target;
      if (!target || !target.closest) return;
      const logoBtn = target.closest('.logo-button');
      const menuItem = target.closest('.user-menu-item');
      const anchor = target.closest('a[href]');
      let trigger = null;
      let replay = null;
      if (logoBtn) {
        trigger = logoBtn;
        replay = () => { window.location.href = '/'; };
      } else if (menuItem && menuItem.textContent && menuItem.textContent.trim() !== 'My Drafts') {
        trigger = menuItem;
        replay = () => {
          document.removeEventListener('click', onCaptureClick, true);
          menuItem.click();
        };
      } else if (anchor) {
        const href = anchor.getAttribute('href') || '';
        if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
        if (anchor.target && anchor.target !== '_self') return;
        trigger = anchor;
        replay = () => { window.location.href = anchor.href; };
      }
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      requestConfirm(replay);
    };
    document.addEventListener('click', onCaptureClick, true);

    return () => {
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('click', onCaptureClick, true);
    };
  }, [hasUnsavedChanges, clearAutosaveTimer]);

  const withUnsavedGuard = useCallback((onProceed) => {
    if (!hasUnsavedChanges) {
      onProceed();
      return;
    }
    setConfirmDialog({
      title: 'Unsaved changes',
      message: 'You have unsaved changes. Leave without saving?',
      confirmLabel: 'Leave',
      onConfirm: () => {
        Object.keys(autosaveTimersRef.current).forEach(clearAutosaveTimer);
        setDirtyById({});
        setSaveStatusById({});
        onProceed();
      },
    });
  }, [clearAutosaveTimer, hasUnsavedChanges]);

  const fetchDetailIfNeeded = useCallback(async (idStr, options = {}) => {
    const { force = false } = options;
    if (detailLoadingById[idStr]) return;
    if (!force && detailById[idStr]) return;

    setDetailLoadingById((prev) => ({ ...prev, [idStr]: true }));
    try {
      const detail = await api.getSavedBrief(idStr);
      setDetailById((prev) => ({ ...prev, [idStr]: detail }));
      setItems((prev) => prev.map((item) => (
        String(item._id) === idStr ? mergeSavedBriefIntoListItem(item, detail) : item
      )));
      if (detail?.status === 'ready') {
        const resolvedResult = resolveSavedBriefResult(detail, comparisonViewById[idStr]);
        setComparisonViewById((prev) => (
          resolvedResult.variant && prev[idStr] !== resolvedResult.variant
            ? { ...prev, [idStr]: resolvedResult.variant }
            : prev
        ));
        setEditDraftById((prev) => ({ ...prev, [idStr]: getEditableDraft(detail, resolvedResult.variant) }));
      } else {
        setEditDraftById((prev) => {
          if (!prev[idStr]) return prev;
          const next = { ...prev };
          delete next[idStr];
          return next;
        });
        setComparisonViewById((prev) => {
          if (!prev[idStr]) return prev;
          const next = { ...prev };
          delete next[idStr];
          return next;
        });
        setDirtyById((prev) => ({ ...prev, [idStr]: false }));
        setSaveStatusById((prev) => {
          if (!prev[idStr]) return prev;
          const next = { ...prev };
          delete next[idStr];
          return next;
        });
        clearAutosaveTimer(idStr);
      }
    } catch (_err) {
      if (!detailById[idStr]) {
        setDetailById((prev) => ({
          ...prev,
          [idStr]: {
            _id: idStr,
            status: 'failed',
            error: { message: 'Failed to load draft details.' },
          },
        }));
      }
    } finally {
      setDetailLoadingById((prev) => ({ ...prev, [idStr]: false }));
    }
  }, [clearAutosaveTimer, comparisonViewById, detailById, detailLoadingById]);

  const canExpandItem = useCallback((idStr) => {
    const row = items.find((item) => String(item._id) === idStr);
    if (!row) return false;
    const detail = detailById[idStr];
    const resolvedStatus = detail?.status || row.status;
    return !isActiveStatus(resolvedStatus);
  }, [items, detailById]);

  const scrollToBrief = useCallback((idStr) => {
    window.setTimeout(() => {
      const element = document.getElementById(`saved-brief-${idStr}`);
      if (element?.scrollIntoView) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }, []);

  const expandTargetBrief = useCallback((idStr, options = {}) => {
    const { forceRefresh = true, shouldScroll = true } = options;
    const proceed = () => {
      setExpandedIds(new Set([idStr]));
      if (forceRefresh) {
        fetchDetailIfNeeded(idStr, { force: true });
      }
      if (shouldScroll) {
        scrollToBrief(idStr);
      }
    };

    withUnsavedGuard(proceed);
  }, [fetchDetailIfNeeded, scrollToBrief, withUnsavedGuard]);

  const toggleExpanded = useCallback((id) => {
    const idStr = String(id);
    const isExpanded = expandedIds.has(idStr);
    if (!isExpanded && !canExpandItem(idStr)) return;

    const proceed = () => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(idStr)) {
          next.delete(idStr);
        } else {
          next.add(idStr);
        }
        return next;
      });
      if (!isExpanded) fetchDetailIfNeeded(idStr, { force: true });
    };

    if (isExpanded && dirtyById[idStr]) {
      setConfirmDialog({
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Collapse this draft without saving?',
        confirmLabel: 'Discard changes',
        onConfirm: () => {
          clearAutosaveTimer(idStr);
          setDirtyById((prev) => ({ ...prev, [idStr]: false }));
          setSaveStatusById((prev) => {
            if (!prev[idStr]) return prev;
            const next = { ...prev };
            delete next[idStr];
            return next;
          });
          proceed();
        },
      });
      return;
    }

    if (!isExpanded) {
      withUnsavedGuard(proceed);
      return;
    }

    proceed();
  }, [expandedIds, fetchDetailIfNeeded, dirtyById, withUnsavedGuard, canExpandItem, clearAutosaveTimer]);

  const refreshAfterMutation = useCallback(async () => {
    await Promise.all([
      loadBriefs({ mode: 'replace', nextOffset: 0 }),
      loadLanguages(),
      loadOwners(),
    ]);
  }, [loadBriefs, loadLanguages, loadOwners]);

  const performSave = useCallback(async (idStr, { source = 'manual' } = {}) => {
    const draft = editDraftByIdRef.current[idStr];
    if (!draft) return;

    clearAutosaveTimer(idStr);
    const snapshot = { header: draft.header, subheader: draft.subheader, brief: draft.brief };
    const isManual = source === 'manual';

    // Guard against the load/normalize race: the body rich-text editor can emit an
    // empty onChange on mount (before the loaded content is applied), which marks the
    // draft dirty and schedules an autosave. Never let that AUTOSAVE persist an empty
    // field over a value the loaded draft still has populated — that silently blanks a
    // fully-generated brief. A manual save is explicit user intent and stays exempt.
    if (!isManual) {
      const loaded = getEditableDraft(detailByIdRef.current[idStr], comparisonViewByIdRef.current[idStr]);
      const wouldBlank = (val, loadedVal) =>
        (!val || !String(val).trim()) && (typeof loadedVal === 'string' && loadedVal.trim());
      if (
        wouldBlank(snapshot.brief, loaded.brief)
        || wouldBlank(snapshot.header, loaded.header)
        || wouldBlank(snapshot.subheader, loaded.subheader)
      ) {
        setSaveStatusById((prev) => {
          if (!prev[idStr]) return prev;
          const nextStatus = { ...prev };
          delete nextStatus[idStr];
          return nextStatus;
        });
        return;
      }
    }

    if (isManual) {
      setSavingById((prev) => ({ ...prev, [idStr]: true }));
    }
    setSaveStatusById((prev) => ({ ...prev, [idStr]: 'saving' }));

    try {
      const resolvedResult = resolveSavedBriefResult(detailByIdRef.current[idStr], comparisonViewByIdRef.current[idStr]);
      const updated = await api.updateSavedBrief(idStr, {
        result: resolvedResult.variant
          ? {
              [resolvedResult.variant]: {
                ...(detailByIdRef.current[idStr]?.result?.[resolvedResult.variant] || {}),
                header: snapshot.header,
                subheader: snapshot.subheader,
                brief: snapshot.brief,
              },
            }
          : {
              header: snapshot.header,
              subheader: snapshot.subheader,
              brief: snapshot.brief,
            },
      });
      setDetailById((prev) => ({ ...prev, [idStr]: updated }));
      const currentDraft = editDraftByIdRef.current[idStr];
      const stillMatches = draftsEqual(currentDraft, snapshot);
      if (stillMatches) {
        setEditDraftById((prev) => ({ ...prev, [idStr]: getEditableDraft(updated, comparisonViewByIdRef.current[idStr]) }));
        setDirtyById((prev) => ({ ...prev, [idStr]: false }));
        setSaveStatusById((prev) => ({ ...prev, [idStr]: 'saved' }));
      } else {
        setSaveStatusById((prev) => ({ ...prev, [idStr]: 'unsaved' }));
        autosaveTimersRef.current[idStr] = window.setTimeout(() => {
          delete autosaveTimersRef.current[idStr];
          performSave(idStr, { source: 'auto' });
        }, AUTOSAVE_DEBOUNCE_MS);
      }
      if (isManual) showToast('Draft saved successfully.');
    } catch (_error) {
      setSaveStatusById((prev) => ({ ...prev, [idStr]: 'failed' }));
      if (isManual) showToast('Failed to save draft changes.', 'error');
    } finally {
      if (isManual) {
        setSavingById((prev) => ({ ...prev, [idStr]: false }));
      }
    }
  }, [clearAutosaveTimer, showToast]);

  const scheduleAutosave = useCallback((idStr) => {
    clearAutosaveTimer(idStr);
    setSaveStatusById((prev) => ({ ...prev, [idStr]: 'unsaved' }));
    autosaveTimersRef.current[idStr] = window.setTimeout(() => {
      delete autosaveTimersRef.current[idStr];
      performSave(idStr, { source: 'auto' });
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [clearAutosaveTimer, performSave]);

  const onEditFieldChange = useCallback((id, field, value) => {
    const idStr = String(id);
    const currentDraft = editDraftById[idStr] || getEditableDraft(detailById[idStr], comparisonViewById[idStr]);
    const next = {
      ...currentDraft,
      [field]: value,
    };
    const detailDraft = getEditableDraft(detailById[idStr], comparisonViewById[idStr]);
    const isDirty = (
      next.header !== detailDraft.header ||
      next.subheader !== detailDraft.subheader ||
      next.brief !== detailDraft.brief
    );
    setEditDraftById((prev) => ({ ...prev, [idStr]: next }));
    setDirtyById((prev) => ({ ...prev, [idStr]: isDirty }));
    if (isDirty) {
      scheduleAutosave(idStr);
    } else {
      clearAutosaveTimer(idStr);
      setSaveStatusById((prev) => {
        if (!prev[idStr]) return prev;
        const nextStatus = { ...prev };
        delete nextStatus[idStr];
        return nextStatus;
      });
    }
  }, [clearAutosaveTimer, comparisonViewById, detailById, editDraftById, scheduleAutosave]);

  const onSave = useCallback((id) => performSave(String(id), { source: 'manual' }), [performSave]);

  const onCopy = useCallback(async (id) => {
    const idStr = String(id);
    const item = items.find((it) => String(it._id) === idStr);
    const detail = detailById[idStr];
    const draft = editDraftById[idStr];
    const { html, text } = buildCopyPayload(item, detail, draft, comparisonViewById[idStr]);
    if (!text) {
      showToast('No content available to copy.', 'error');
      return;
    }
    try {
      await copyRichText({ html, text });
      showToast('Copied to clipboard.');
    } catch (_error) {
      showToast('Failed to copy content.', 'error');
    }
  }, [comparisonViewById, items, detailById, editDraftById, showToast]);

  const onOpenTranslate = useCallback((id) => {
    const idStr = String(id);
    const item = items.find((it) => String(it._id) === idStr);
    setTranslateModal({ id: idStr, target: item?.language || 'English' });
  }, [items]);

  const onCloseTranslateModal = useCallback(() => {
    setTranslateModal((prev) => {
      // After the modal closes, scroll the brief row back into view at the
      // top of the viewport so the user doesn't lose context if the page
      // shifted while the modal was open.
      if (prev?.id) scrollToBrief(prev.id);
      return null;
    });
  }, [scrollToBrief]);

  // Local name → code map shared by translate-flow callers.
  const runTranslate = useCallback(async (idStr, targetLanguageName) => {
    const detail = detailById[idStr];
    const draft = editDraftById[idStr] || getEditableDraft(detail, comparisonViewById[idStr]);
    const resolvedResult = resolveSavedBriefResult(detail, comparisonViewById[idStr]);
    const variant = resolvedResult?.variant || null;
    const resultContent = resolvedResult?.content || {};
    const item = items.find((it) => String(it._id) === idStr);

    const header = draft?.header || '';
    const subheader = draft?.subheader || '';
    // Keep the entire body HTML — including <a> tags and source URLs — so the
    // translator preserves links. The Sources footer (Sources: / Quellen: /
    // מקורות: etc.) is left in so its labels translate naturally too; the
    // backend prompt is instructed to keep all HTML/URLs exactly intact.
    const bodyHtml = String(draft?.brief || '');
    const sourceTitles = Array.isArray(resultContent.titles)
      ? resultContent.titles.filter((t) => typeof t === 'string' && t.trim())
      : [];

    // From the shared registry rather than an 18-name local table whose
    // `|| 'en'` fallback silently translated anything it did not know into
    // English.
    const targetCode = toIso2(targetLanguageName) || 'en';
    const sourceLangName = item?.language || 'English';
    const sourceCode = toIso2(sourceLangName) || 'en';

    // Remember whether the row was open so we can re-expand it after the
    // translation completes; collapse it now so the user sees the row-level
    // "Translating..." spinner without the stale source-language content
    // shown underneath.
    const wasExpanded = expandedIds.has(idStr);
    if (wasExpanded) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(idStr);
        return next;
      });
    }

    // Fast path: if we've already produced this brief in the target language
    // (e.g. user translated EN → DE earlier and is now hopping back to EN),
    // restore the cached snapshot without an API call. This is also what
    // covers "fetch the original English one if it already exists".
    const cachedForTarget = originalsByLangById[idStr]?.[targetCode];
    if (cachedForTarget) {
      setTranslatingById((prev) => ({ ...prev, [idStr]: true }));
      try {
        const next = {
          header: cachedForTarget.header || '',
          subheader: cachedForTarget.subheader || '',
          brief: normalizeBriefBodyForEditor(cachedForTarget.brief || ''),
        };
        setEditDraftById((prev) => ({ ...prev, [idStr]: next }));
        if (variant && Array.isArray(cachedForTarget.titles)) {
          setDetailById((prev) => {
            const prevDetail = prev[idStr];
            if (!prevDetail?.result) return prev;
            const prevVariantContent = prevDetail.result[variant] || prevDetail.result;
            const updatedVariantContent = { ...prevVariantContent, titles: cachedForTarget.titles };
            return {
              ...prev,
              [idStr]: {
                ...prevDetail,
                result: prevDetail.result[variant]
                  ? { ...prevDetail.result, [variant]: updatedVariantContent }
                  : updatedVariantContent,
              },
            };
          });
        }
        setDirtyById((prev) => ({ ...prev, [idStr]: true }));
        showToast(`Restored ${targetLanguageName} version.`);
        return;
      } finally {
        setTranslatingById((prev) => ({ ...prev, [idStr]: false }));
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.add(idStr);
          return next;
        });
      }
    }

    // Build the translate payload. We send everything as a single batch so
    // related strings translate with consistent terminology.
    const titlesCount = sourceTitles.length;
    const payload = [header, subheader, bodyHtml, ...sourceTitles];
    if (payload.every((s) => !s || !String(s).trim())) {
      showToast('Nothing to translate.', 'error');
      return;
    }

    setTranslatingById((prev) => ({ ...prev, [idStr]: true }));
    try {
      // `draft: true` gives the translation the same hygiene a generated draft
      // gets (no asterisks, no letters from another script). The Create Brief
      // editor already sent it; this one did not, so every draft translated
      // here skipped the 2 Nov rules (Orr, 3 Nov).
      const translated = await api.translateFast({ texts: payload, target: targetCode, draft: true });
      if (!Array.isArray(translated) || translated.length !== payload.length) {
        throw new Error('Unexpected translation response');
      }
      const [tHeader, tSubheader, tBody, ...tTitles] = translated;
      const next = {
        header: tHeader || '',
        subheader: tSubheader || '',
        brief: normalizeBriefBodyForEditor(tBody || ''),
      };

      // Cache the current (pre-translation) snapshot under its own language
      // code so we can hop back to it without another API call later.
      setOriginalsByLangById((prev) => {
        const forBrief = prev[idStr] || {};
        if (forBrief[sourceCode]) return prev; // first snapshot wins
        return {
          ...prev,
          [idStr]: {
            ...forBrief,
            [sourceCode]: {
              titles: sourceTitles,
              header,
              subheader,
              brief: bodyHtml,
            },
          },
        };
      });

      setEditDraftById((prev) => ({ ...prev, [idStr]: next }));
      if (variant && titlesCount > 0) {
        setDetailById((prev) => {
          const prevDetail = prev[idStr];
          if (!prevDetail?.result) return prev;
          const prevVariantContent = prevDetail.result[variant] || prevDetail.result;
          const updatedVariantContent = { ...prevVariantContent, titles: tTitles };
          return {
            ...prev,
            [idStr]: {
              ...prevDetail,
              result: prevDetail.result[variant]
                ? { ...prevDetail.result, [variant]: updatedVariantContent }
                : updatedVariantContent,
            },
          };
        });
      }
      setDirtyById((prev) => ({ ...prev, [idStr]: true }));
      showToast(`Translated to ${targetLanguageName}. Review and click Save.`);
    } catch (_error) {
      showToast('Translation failed.', 'error');
    } finally {
      setTranslatingById((prev) => ({ ...prev, [idStr]: false }));
      // Re-expand the row (animated via CSS keyframe on the wrap).
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.add(idStr);
        return next;
      });
    }
  }, [
    detailById, editDraftById, comparisonViewById, expandedIds, items,
    originalsByLangById, showToast,
  ]);

  const onConfirmTranslate = useCallback(() => {
    if (!translateModal) return;
    const { id, target } = translateModal;
    setTranslateModal(null);
    // Scroll the brief into view as the modal closes so the user sees the
    // "Translating..." spinner appear in the row header.
    scrollToBrief(id);
    runTranslate(id, target);
  }, [translateModal, runTranslate, scrollToBrief]);

  const onComparisonViewChange = useCallback((id, nextVariant) => {
    const idStr = String(id);
    setComparisonViewById((prev) => ({ ...prev, [idStr]: nextVariant }));
    const detail = detailById[idStr];
    if (detail?.status === 'ready') {
      setEditDraftById((prev) => ({ ...prev, [idStr]: getEditableDraft(detail, nextVariant) }));
      setDirtyById((prev) => ({ ...prev, [idStr]: false }));
      clearAutosaveTimer(idStr);
      setSaveStatusById((prev) => {
        if (!prev[idStr]) return prev;
        const next = { ...prev };
        delete next[idStr];
        return next;
      });
    }
  }, [clearAutosaveTimer, detailById]);

  /**
   * PRD §C3 — Send to client.
   *
   * "Resolves existing bucket primary contact and additional recipients;
   *  creates an immutable client-facing version; sends its public link through
   *  the existing templated delivery infrastructure."
   *
   * Deliberately reuses the bucket contact model rather than asking the author
   * for an address. §C3 and §7 both rule out a competing contacts system, and a
   * free-text recipient field is how one starts: the addresses diverge from the
   * bucket's, nobody notices, and a client stops receiving things.
   */
  const onSendToClient = useCallback(async (id) => {
    const idStr = String(id);
    try {
      const res = await api.sendDraftToClient(idStr, { silent: true });
      if (res?.error) { showToast(res.error, 'error'); return; }
      showToast(
        res?.recipientCount > 1
          ? `Sent to ${res.recipientCount} recipients.`
          : 'Sent to the client.',
        'success',
      );
    } catch (error) {
      // The commonest failure by far is a bucket with no resolvable recipient,
      // and the server says so specifically — repeat it rather than flattening
      // every cause into "failed".
      showToast(
        error?.response?.data?.error || 'Could not send this draft to the client.',
        'error',
      );
    }
  }, [showToast]);

  const onPublishCms = useCallback(async (id) => {
    const idStr = String(id);
    try {
      const clients = await api.getPublishClients();
      setPublishClients(clients || []);
      if (clients?.length > 0) setSelectedClient(clients[0]._id);
      setPublishResult(null);
      setPublishStatus('draft');
      setPublishDialog({ id: idStr });
    } catch (_error) {
      showToast('Failed to load publish clients.', 'error');
    }
  }, [showToast]);

  const startLifecycleAction = useCallback(async (mode, id, settingsValues) => {
    const idStr = String(id);
    const isRegenerateCopy = mode === 'regenerate_copy';
    const { overwriteExisting: _overwriteExisting, ...baseRequestValues } = settingsValues || {};
    const requestValues = isRegenerateCopy
      ? { ...baseRequestValues, createNewCopy: true }
      : baseRequestValues;
    try {
      if (mode === 'regenerate' || mode === 'regenerate_copy') {
        await api.regenerateSavedBrief(idStr, requestValues);
      } else {
        await api.retrySavedBrief(idStr, requestValues);
      }
      if (!isRegenerateCopy) {
        setExpandedIds((prev) => {
          if (!prev.has(idStr)) return prev;
          const next = new Set(prev);
          next.delete(idStr);
          return next;
        });
        setDirtyById((prev) => ({ ...prev, [idStr]: false }));
        clearAutosaveTimer(idStr);
        setSaveStatusById((prev) => {
          if (!prev[idStr]) return prev;
          const next = { ...prev };
          delete next[idStr];
          return next;
        });
      }
      showToast(
        mode === 'regenerate_copy'
          ? 'New regenerated draft started.'
          : mode === 'regenerate'
            ? 'Draft regeneration started.'
            : 'Draft retry started.'
      );
      await refreshAfterMutation();
    } catch (_error) {
      showToast(
        mode === 'regenerate' || mode === 'regenerate_copy'
          ? 'Failed to start regeneration.'
          : 'Failed to retry draft.',
        'error'
      );
    }
  }, [clearAutosaveTimer, refreshAfterMutation, showToast]);

  const openLifecycleSettings = useCallback((mode, id) => {
    const idStr = String(id);
    const detail = detailById[idStr];
    const item = items.find((it) => String(it._id) === idStr);
    if (!detail) return;
    setGenerationModal({
      mode,
      id: idStr,
      title: detail?.storyTitle || item?.storyTitle || 'Draft',
      values: buildGenerationSettings(detail, item),
    });
  }, [detailById, items]);

  const onRegenerate = useCallback((id) => {
    openLifecycleSettings('regenerate', id);
  }, [openLifecycleSettings]);

  const onRetry = useCallback((id) => {
    openLifecycleSettings('retry', id);
  }, [openLifecycleSettings]);

  const onDelete = useCallback((id) => {
    const idStr = String(id);
    setConfirmDialog({
      title: 'Archive draft',
      message: 'Archive this draft? You can restore it later from the Archived filter.',
      confirmLabel: 'Archive',
      onConfirm: async () => {
        try {
          await api.softDeleteSavedBrief(idStr);
          showToast('Draft archived.');
          await refreshAfterMutation();
        } catch (_error) {
          showToast('Failed to archive draft.', 'error');
        }
      },
    });
  }, [refreshAfterMutation, showToast]);

  const onRestore = useCallback((id) => {
    const idStr = String(id);
    setConfirmDialog({
      title: 'Restore draft',
      message: 'Restore this archived draft?',
      confirmLabel: 'Restore',
      onConfirm: async () => {
        try {
          await api.restoreSavedBrief(idStr);
          showToast('Draft restored.');
          await refreshAfterMutation();
        } catch (_error) {
          showToast('Failed to restore draft.', 'error');
        }
      },
    });
  }, [refreshAfterMutation, showToast]);

  const onPermanentDelete = useCallback((id) => {
    const idStr = String(id);
    setConfirmDialog({
      title: 'Permanently delete draft',
      message: 'This draft will be permanently deleted and cannot be restored.',
      confirmLabel: 'Permanently delete',
      onConfirm: async () => {
        try {
          await api.permanentlyDeleteSavedBrief(idStr);
          showToast('Draft permanently deleted.');
          await refreshAfterMutation();
        } catch (_error) {
          showToast('Failed to permanently delete draft.', 'error');
        }
      },
    });
  }, [refreshAfterMutation, showToast]);

  const resetAll = useCallback(() => {
    withUnsavedGuard(() => {
      setDraftSearch('');
      setSubmittedQuery('');
      setFilters(MY_BRIEF_DEFAULT_FILTERS);
      setExpandedIds(new Set());
      setDetailById({});
      setDetailLoadingById({});
      setEditDraftById({});
      setComparisonViewById({});
      setDirtyById({});
      setSaveStatusById({});
    });
  }, [withUnsavedGuard]);

  const onFilterChange = useCallback((key, value) => {
    withUnsavedGuard(() => setFilters((prev) => ({ ...prev, [key]: value })));
  }, [withUnsavedGuard]);

  const onSearchSubmit = useCallback((event) => {
    event.preventDefault();
    withUnsavedGuard(() => setSubmittedQuery(draftSearch.trim()));
  }, [draftSearch, withUnsavedGuard]);

  const onClearDraft = useCallback(() => {
    setDraftSearch('');
  }, []);

  useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  useEffect(() => {
    loadOwners();
  }, [loadOwners]);

  useEffect(() => {
    setExpandedIds(new Set());
    setDetailById({});
    setDetailLoadingById({});
    setEditDraftById({});
    setComparisonViewById({});
    setDirtyById({});
    setSaveStatusById({});
    Object.values(autosaveTimersRef.current).forEach((timer) => window.clearTimeout(timer));
    autosaveTimersRef.current = {};
    loadBriefs({ mode: 'replace', nextOffset: 0 });
  }, [filters, submittedQuery, loadBriefs]);

  const selectedOwner = useMemo(
    () => savedBriefOwners.find((owner) => owner.userId === filters.ownerId) || null,
    [filters.ownerId, savedBriefOwners]
  );
  const isReadOnlyOwnerView = isAdmin && filters.ownerId !== 'mine';

  useEffect(() => {
    const handler = (event) => {
      const readyItem = event?.detail;
      const idStr = String(readyItem?._id || '');
      if (!idStr) return;

      let itemPresent = false;
      setItems((prev) => {
        itemPresent = prev.some((item) => String(item._id) === idStr);
        return prev.map((item) => (
          String(item._id) === idStr
            ? { ...item, status: 'ready' }
            : item
        ));
      });

      if (!itemPresent) {
        loadBriefs({ mode: 'replace', nextOffset: 0 });
      }

      if (readyItem?.openTarget) {
        expandTargetBrief(idStr, { forceRefresh: true, shouldScroll: true });
        return;
      }

      fetchDetailIfNeeded(idStr, { force: true });
    };

    window.addEventListener(SAVED_BRIEF_READY_EVENT, handler);
    return () => window.removeEventListener(SAVED_BRIEF_READY_EVENT, handler);
  }, [expandTargetBrief, fetchDetailIfNeeded, loadBriefs]);

  useEffect(() => {
    const handler = () => {
      loadBriefs({ mode: 'replace', nextOffset: 0 });
    };
    window.addEventListener(SAVED_BRIEF_CREATED_EVENT, handler);
    return () => window.removeEventListener(SAVED_BRIEF_CREATED_EVENT, handler);
  }, [loadBriefs]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const expandId = params.get('expand');
    if (!expandId) {
      lastHandledExpandRequestRef.current = '';
      return;
    }
    if (!items.length) return;
    const requestKey = `${location.key || ''}:${String(expandId)}`;
    if (lastHandledExpandRequestRef.current === requestKey) return;

    const idStr = String(expandId);
    const exists = items.some((item) => String(item._id) === idStr);
    if (exists) {
      if (!canExpandItem(idStr)) {
        fetchDetailIfNeeded(idStr, { force: true });
        return;
      }
      lastHandledExpandRequestRef.current = requestKey;
      expandTargetBrief(idStr, { forceRefresh: true, shouldScroll: true });
    }
  }, [items, location.key, location.search, fetchDetailIfNeeded, canExpandItem, expandTargetBrief]);

  useEffect(() => {
    expandedIds.forEach((idStr) => {
      const row = items.find((item) => String(item._id) === String(idStr));
      const detail = detailById[idStr];
      if (!row) return;
      const detailMissing = !detail;
      const detailOutdated = detail && detail.status !== row.status;
      const rowStableButDetailActive = detail && !isActiveStatus(row.status) && isActiveStatus(detail.status);
      if (detailMissing || detailOutdated || rowStableButDetailActive) {
        fetchDetailIfNeeded(String(idStr), { force: true });
      }
    });
  }, [expandedIds, items, detailById, fetchDetailIfNeeded]);

  useEffect(() => {
    setExpandedIds((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((idStr) => {
        const row = items.find((item) => String(item._id) === String(idStr));
        if (row && !isActiveStatus(row.status)) {
          next.add(String(idStr));
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [items]);

  useEffect(() => {
    if (!loadMoreSentinelRef.current || !hasMore || loadingMore || loadingInitial) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasMore && !loadingMore && !loadingInitial) {
          loadBriefs({ mode: 'append', nextOffset: offset });
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadingInitial, loadBriefs, offset]);

  const visibleHasActiveStatus = useMemo(
    () => items.some((item) => isActiveStatus(item.status)),
    [items]
  );

  useEffect(() => {
    if (!visibleHasActiveStatus) return undefined;

    const intervalId = window.setInterval(() => {
      loadBriefs({
        mode: 'background_merge',
        nextOffset: 0,
        limitOverride: Math.max(items.length, PAGE_SIZE),
      });
      setNowMs(Date.now());
    }, 30 * 1000);

    return () => window.clearInterval(intervalId);
  }, [items.length, loadBriefs, visibleHasActiveStatus]);

  return (
    <div className="my-briefs-page-surface">
      <section className="my-briefs-page-shell" data-testid="my-briefs-page">
      <MyBriefsFilters
        isAdmin={isAdmin}
        ownerLabel={selectedOwner?.label || ''}
        draftSearch={draftSearch}
        onDraftSearchChange={setDraftSearch}
        onSearchSubmit={onSearchSubmit}
        onClearDraft={onClearDraft}
        filters={filters}
        languages={savedBriefLanguages}
        owners={savedBriefOwners}
        onFilterChange={onFilterChange}
        onReset={resetAll}
      />

      {error ? <div className="my-briefs-error">{error}</div> : null}

      {loadingInitial ? (
        <div className="my-briefs-page-loader" data-testid="my-briefs-skeleton-loading">
          <span className="my-briefs-page-loader-spinner" aria-hidden="true" />
          <span className="my-briefs-page-loader-text">Loading drafts...</span>
        </div>
      ) : (
        <MyBriefsList
          items={items}
          nowMs={nowMs}
          currentUserEmail={userInfo?.email || ''}
          showOwnerMeta={Boolean(selectedOwner)}
          forceReadOnlyOwnerView={isReadOnlyOwnerView}
          expandedIds={expandedIds}
          onToggleExpanded={toggleExpanded}
          detailById={detailById}
          detailLoadingById={detailLoadingById}
          editDraftById={editDraftById}
          comparisonViewById={comparisonViewById}
          dirtyById={dirtyById}
          savingById={savingById}
          saveStatusById={saveStatusById}
          onComparisonViewChange={onComparisonViewChange}
          onEditFieldChange={onEditFieldChange}
          onSave={onSave}
          onCopy={onCopy}
          onPublishCms={isAdmin ? onPublishCms : null}
          onSendToClient={isAdmin ? onSendToClient : null}
          onRegenerate={onRegenerate}
          onRetry={onRetry}
          onDelete={onDelete}
          onRestore={onRestore}
          onPermanentDelete={onPermanentDelete}
          onOpenTranslate={onOpenTranslate}
          translatingById={translatingById}
          sentinelRef={loadMoreSentinelRef}
          loadingMore={loadingMore}
          hasMore={hasMore}
        />
      )}

      {toast ? (
        <div className={`my-briefs-toast ${toast.type || 'success'}`}>{toast.message}</div>
      ) : null}

      {confirmDialog ? (
        <div className="modal" data-testid="my-briefs-confirm-modal">
          <div className="modal-content my-briefs-modal-content">
            <div className="my-briefs-modal-header">
              <h3 className="my-briefs-modal-title my-briefs-modal-title-centered">{confirmDialog.title}</h3>
            </div>
            <div className="my-briefs-modal-body my-briefs-modal-body-centered">
              <p className="my-briefs-modal-message my-briefs-modal-message-centered">{confirmDialog.message}</p>
              <div className="my-briefs-modal-actions my-briefs-modal-actions-centered">
                <button type="button" className="my-briefs-action-button" onClick={() => setConfirmDialog(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`my-briefs-action-button ${
                    /(delete|archive)/i.test(confirmDialog.confirmLabel || '') ? 'danger' : 'primary'
                  }`}
                  onClick={async () => {
                    const callback = confirmDialog.onConfirm;
                    setConfirmDialog(null);
                    if (callback) await callback();
                  }}
                >
                  {confirmDialog.confirmLabel || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {publishDialog ? (
        <div className="modal" data-testid="my-briefs-publish-modal">
          <div className="modal-content my-briefs-modal-content">
            <div className="my-briefs-modal-header">
              <h3 className="my-briefs-modal-title my-briefs-modal-title-centered">Publish Draft to CMS</h3>
            </div>
            <div className="my-briefs-modal-body my-briefs-modal-body-centered">
              {publishResult ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: publishResult.status === 'failed' ? '#d32f2f' : '#2e7d32', fontWeight: 'bold', fontSize: '16px' }}>
                    {publishResult.status === 'failed' ? 'Publish Failed' : 'Published Successfully'}
                  </p>
                  {publishResult.externalId ? <p style={{ color: '#666', fontSize: '13px' }}>External ID: {publishResult.externalId}</p> : null}
                  {publishResult.error ? <p style={{ color: '#d32f2f', fontSize: '13px' }}>{publishResult.error}</p> : null}
                  <div className="my-briefs-modal-actions my-briefs-modal-actions-centered">
                    <button type="button" className="my-briefs-action-button" onClick={() => { setPublishDialog(null); setPublishResult(null); }}>Close</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '12px', textAlign: 'left' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>Target:</label>
                    <select value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}>
                      {publishClients.map((c) => <option key={c._id} value={c._id}>{c.displayName}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: '16px', textAlign: 'left' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>Status:</label>
                    <select value={publishStatus} onChange={(e) => setPublishStatus(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}>
                      <option value="draft">Draft</option>
                      <option value="published">Published (Live)</option>
                    </select>
                  </div>
                  <div className="my-briefs-modal-actions my-briefs-modal-actions-centered">
                    <button type="button" className="my-briefs-action-button" onClick={() => setPublishDialog(null)}>Cancel</button>
                    <button
                      type="button"
                      className="my-briefs-action-button primary"
                      disabled={isPublishing || !selectedClient}
                      onClick={async () => {
                        setIsPublishing(true);
                        try {
                          const idStr = publishDialog.id;
                          const item = items.find((it) => String(it._id) === idStr);
                          const detail = detailById[idStr];
                          const draft = editDraftById[idStr];
                          const resolvedResult = resolveSavedBriefResult(detail, comparisonViewById[idStr]);
                          const result = await api.publishBrief({
                            clientId: selectedClient,
                            title: draft?.header || resolvedResult.content?.header || '',
                            subheader: draft?.subheader || resolvedResult.content?.subheader || '',
                            body: draft?.brief || resolvedResult.content?.brief || '',
                            language: detail?.language || item?.language || 'en',
                            storyId: item?.storyId,
                            storyTitle: item?.storyTitle || '',
                            status: publishStatus,
                          });
                          setPublishResult(result);
                        } catch (err) {
                          setPublishResult({ status: 'failed', error: err.message || 'Unknown error' });
                        } finally {
                          setIsPublishing(false);
                        }
                      }}
                    >
                      {isPublishing ? 'Publishing...' : 'Publish'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <BriefGenerationSettingsModal
        isOpen={Boolean(generationModal)}
        title={generationModal?.title || 'Draft'}
        canEdit={canEdit}
        canSelectGenerationMethod={canCreateBrief}
        languages={Array.from(new Set([...OUTPUT_LANGUAGE_NAMES, ...(savedBriefLanguages || []), generationModal?.values?.language].filter(Boolean)))}
        values={generationModal?.values || {
          promptType: 'DEFAULT_BRIEF_2_CITATIONS',
          language: 'English',
          model: 'gpt-5',
          translationModel: 'gpt',
          generationMethod: 'claims',
          overwriteExisting: false,
        }}
        onChange={(key, value) => {
          setGenerationModal((prev) => {
            if (!prev) return prev;
            return { ...prev, values: { ...prev.values, [key]: value } };
          });
        }}
        onCancel={() => {
          const current = generationModal;
          setGenerationModal(null);
          if (current?.id) scrollToBrief(current.id);
        }}
        onContinue={() => {
          const current = generationModal;
          setGenerationModal(null);
          if (!current) return;
          // Scroll the brief row back into view so the user sees the
          // "Regenerating..." spinner kick in without losing context.
          scrollToBrief(current.id);
          if (current.mode === 'regenerate') {
            startLifecycleAction(current.values.overwriteExisting ? 'regenerate' : 'regenerate_copy', current.id, current.values);
            return;
          }
          startLifecycleAction('retry', current.id, current.values);
        }}
        showCreateNewCopyOption={generationModal?.mode === 'regenerate'}
        continueLabel={generationModal?.mode === 'regenerate' ? 'Continue' : 'Retry'}
        modalTestId="my-briefs-settings-modal"
      />

      {translateModal ? (
        <div
          className="my-briefs-translate-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="my-briefs-translate-modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) onCloseTranslateModal(); }}
        >
          <div className="my-briefs-translate-modal">
            <div className="my-briefs-translate-modal-header">
              <h3 id="my-briefs-translate-modal-title">Translate brief</h3>
            </div>
            <div className="my-briefs-translate-modal-body">
              <label htmlFor="my-briefs-translate-modal-target">Target language</label>
              <select
                id="my-briefs-translate-modal-target"
                value={translateModal.target}
                onChange={(e) => setTranslateModal((prev) => prev ? { ...prev, target: e.target.value } : prev)}
              >
                {/* Deliverable languages only — the source list has 89, but a
                    target we cannot translate into silently yields English. */}
                {OUTPUT_LANGUAGE_NAMES.map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
              <p style={{ marginTop: '14px', fontSize: '13px', color: '#3C4A5E' }}>
                We&apos;ll translate the current header, subheader, and body and
                keep all inline links and sources intact. You can review and
                edit the result before saving.
              </p>
              <div className="my-briefs-translate-modal-footer">
                <button
                  type="button"
                  className="my-briefs-translate-modal-btn cancel"
                  onClick={onCloseTranslateModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="my-briefs-translate-modal-btn confirm"
                  onClick={onConfirmTranslate}
                >
                  Translate
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      </section>
    </div>
  );
}

export default MyBriefsPage;
